import { isVerifiedSanityCategory } from '../../lib/preservedUserDecisions';
/**
 * ReportQualityAuditSection — per-report quality review, hosted on the
 * site AUDIT tab (William 2026-07-28: report quality IS an audit concern;
 * the reader is the printable deliverable).
 *
 * Replaces the reader-side ReportInsightsTray as the home of:
 *  - the sanity-finding review flow (acknowledge / apply-directive) —
 *    resolutions persist onto the report doc and feed reruns, so this
 *    surface is the recovery path behind "failed delivery validation";
 *  - QualityPanel (AI review, fact-check retry, critical-document
 *    ceiling, SED integrity);
 *  - GenerationCostPanel (admin-only telemetry).
 *
 * Deep-linkable: `/sites/:id?tab=audit&report=<reportId>` preselects a
 * report (the report header's "Review quality details" button links here).
 * The param is read once per mount; the picker owns selection afterwards.
 */
import React from 'react';
import { FileSearch } from 'lucide-react';
import { Button } from '../ui/button';
import type { SanityFinding, SiteRagReport } from '../../types/siteRag';
import { useStore } from '../../store';
import {
  SanityCheckCard,
  type FindingsFilter,
  type SanityApplyNowInput,
  type SanityResolutionChange,
} from './SanityCheckCard';
import { QualityPanel } from './QualityPanel';
import { GenerationCostPanel } from './report/GenerationCostPanel';
import { TruthResolutionDialog, type TruthResolutionApplyCorrectionItem } from './report/TruthResolutionDialog';
import { emitAuditEvent } from '../../lib/auditLogClient';
import { isDatacrateAdmin } from '../../lib/isDatacrateAdmin';
import {
  areSectionAndFieldPathCompatible,
  isDiagnosticComplaintNotReplacement,
  resolveReportFieldPathForCorrection,
} from '../../lib/reportGeneration/applySourceSupportedAuditCorrection';
import { authedFetch } from '../../lib/authedFetch';
import {
  collectNarrativeStrings,
  isFalsePositiveQueueDurationFindingText,
} from '../../lib/audit/queueDurationCheck';
import { isReviewIssueAlreadyQualifiedInText } from '../../lib/audit/reviewIssueSurvival';
import { sanityFindingIdentity } from '../../lib/reportUserState';
import { buildSanityResolutionAuditEvent } from '../../lib/sanityResolutionAudit';
import {
  computeAuditOverridesFingerprint,
  computeAuditOverridesVersion,
  filterUnsettledTruthQuestions,
  generateAuditOperationId,
  resolveCurrentEffectiveAmendments,
} from '../../lib/audit/truthAttestations';
import { useUrlSearchParamSync } from '../../lib/useUrlSearchParamSync';

export interface ReportQualityAuditSectionProps {
  siteReports: SiteRagReport[];
  /** Owning site id, including for legacy/name-matched reports without siteId. */
  siteId?: string;
  /** Opens the selected report in the reader (threaded from the tab host). */
  onViewReport?: (reportId: string) => void;
  /**
   * Findings-ledger cross-link ("Review & apply fix" on an R-8/R-9/R-15
   * finding): selects this report and scrolls the section into view.
   * `focusNonce` re-triggers the effect on repeat clicks for the same report.
   */
  focusReportId?: string | null;
  focusNonce?: number;
  /** Regenerate the selected report after a truth question is retracted. */
  siteDocuments?: ReadonlyArray<{ id: string; name?: string; fileName?: string; siteId?: string; category?: string }>;
  onRerunReport?: (reportId: string) => void;
}

function enrichCitationFromReportReferences(
  report: SiteRagReport,
  rawCitation: string,
): string {
  const trimmed = rawCitation.trim() || '[Ref 1]';
  const refs = Array.isArray(report.references) ? report.references : [];
  if (refs.length === 0) return trimmed;
  const match = trimmed.match(/\[\s*Ref\s+(\d+)/i);
  const idx = match ? Number(match[1]) - 1 : 0;
  const ref = (refs[idx] ?? refs[0]) as unknown as Record<string, unknown> | undefined;
  if (!ref) return trimmed;
  const anchor = [ref.title, ref.source, ref.field].find(
    (v): v is string => typeof v === 'string' && v.trim().length >= 4,
  );
  if (!anchor) return trimmed;
  if (trimmed.toLowerCase().includes(anchor.trim().toLowerCase())) return trimmed;
  return `${trimmed} — ${anchor.trim()}`;
}

export function ReportQualityAuditSection({
  siteReports,
  siteId,
  siteDocuments,
  onViewReport,
  focusReportId,
  focusNonce,
  onRerunReport,
}: ReportQualityAuditSectionProps) {
  const updateRagReport = useStore(s => s.updateRagReport);
  const resolveSanityFinding = useStore(s => s.resolveSanityFinding);
  const applySourceSupportedCorrectionToReport = useStore(
    s => s.applySourceSupportedCorrectionToReport,
  );
  const userRole = useStore(s => s.userRole);
  const companyId = useStore(s => s.companyId);
  const companyRole = useStore(s => s.companyRole);
  // Cost telemetry (dollar figures) is Datacrate-internal — PLATFORM-admin
  // gate, same predicate the reader used (never tenant company-admins).
  const isAdmin = isDatacrateAdmin({ userRole, companyId, companyRole });
  // Router-independent param read (this section renders in tests without a
  // Router); one-shot at mount — the picker owns selection afterwards.
  const requestedId = React.useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('report'); } catch { return null; }
  }, []);
  const requestedReview = React.useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('review'); } catch { return null; }
  }, []);
  // Capture the one-shot intent above, then consume it through the same
  // Router-aware URL mirror as the owning page. A raw history.replaceState
  // leaves React Router's location stale, allowing a later tab sync to restore
  // `review=truth` and reopen the dialog after the user has already closed it.
  useUrlSearchParamSync(
    'review',
    requestedReview === 'truth' ? null : requestedReview,
  );
  const [selectedId, setSelectedId] = React.useState<string | null>(
    () => (requestedId && siteReports.some(r => r.id === requestedId)
      ? requestedId
      : siteReports[0]?.id ?? null),
  );
  const report = siteReports.find(r => r.id === selectedId) ?? siteReports[0] ?? null;
  // A report-specific handoff remains report-specific after competing parent
  // URL mirrors (tab/guided/focus) settle. Bare Audit links stay bare until
  // the user explicitly changes the picker; that choice then becomes a
  // durable, shareable report context as well.
  const [hasDurableReportContext, setHasDurableReportContext] = React.useState(
    () => requestedId != null,
  );
  useUrlSearchParamSync(
    'report',
    hasDurableReportContext ? report?.id ?? requestedId : null,
  );
  const effectiveSiteId = siteId ?? report?.siteId;
  const currentSite = useStore(s => (
    effectiveSiteId
      ? s.siteRag.sites.find(site => site.id === effectiveSiteId)
      : undefined
  ));
  const siteOverrides = currentSite?.auditOverrides;
  const currentSedId =
    currentSite?.sedId ??
    (currentSite as { sed?: { id?: string } } | undefined)?.sed?.id ??
    (report as { sedId?: string } | null)?.sedId ??
    currentSite?.sedAmendmentHistory?.find(a => a.status === 'applied')?.sedId;
  const currentSedVersion =
    typeof currentSite?.sedVersion === 'number'
      ? currentSite.sedVersion
      : typeof (currentSite as { sed?: { version?: number } } | undefined)?.sed?.version === 'number'
        ? (currentSite as { sed?: { version?: number } }).sed!.version
        : undefined;
  const storeAmendments = useStore(s => s.amendmentState?.amendments);
  const appliedCurrentAmendments = React.useMemo(() => {
    if (!currentSedId) return [];
    const combined = [
      ...(currentSite?.sedAmendmentHistory ?? []),
      ...(storeAmendments ?? []),
    ];
    return resolveCurrentEffectiveAmendments(
      combined,
      currentSedId,
      currentSedVersion,
    ).effectiveStructured;
  }, [currentSite?.sedAmendmentHistory, storeAmendments, currentSedId, currentSedVersion]);
  const scopedSiteDocuments = React.useMemo(() => {
    const seen = new Set<string>();
    const docs: Array<{ id: string; name: string; siteId?: string; category?: string }> = [];
    const pushDoc = (
      d:
        | { id?: string; name?: string; fileName?: string; siteId?: string; category?: string }
        | undefined
        | null,
    ) => {
      if (!d || typeof d.id !== 'string' || !d.id || seen.has(d.id)) return;
      if (effectiveSiteId && d.siteId && d.siteId !== effectiveSiteId) return;
      const resolvedName =
        typeof d.name === 'string' && d.name.trim()
          ? d.name
          : typeof d.fileName === 'string' && d.fileName.trim()
            ? d.fileName
            : d.id;
      seen.add(d.id);
      docs.push({
        id: d.id,
        name: resolvedName,
        ...(d.siteId ? { siteId: d.siteId } : {}),
        ...(d.category ? { category: d.category } : {}),
      });
    };
    for (const d of siteDocuments ?? []) pushDoc(d);
    for (const d of currentSite?.documents ?? []) pushDoc(d as { id?: string; name?: string; fileName?: string; siteId?: string; category?: string });
    for (const r of siteReports) {
      if (effectiveSiteId && r.siteId && r.siteId !== effectiveSiteId) continue;
      for (const d of r.documents ?? []) pushDoc(d as { id?: string; name?: string; fileName?: string; siteId?: string; category?: string });
    }
    return docs;
  }, [siteDocuments, currentSite?.documents, siteReports, effectiveSiteId]);
  const truthQuestions = React.useMemo(
    () => filterUnsettledTruthQuestions(
      report?.qaFindings?.truthQuestions,
      siteOverrides,
      report?.reportType,
      appliedCurrentAmendments,
      currentSedId,
      currentSedVersion,
      report?.qaFindings as Record<string, unknown> | undefined,
    ),
    [
      report?.qaFindings,
      report?.reportType,
      siteOverrides,
      appliedCurrentAmendments,
      currentSedId,
      currentSedVersion,
    ],
  );
  const [truthDialogOpen, setTruthDialogOpen] = React.useState(false);
  const findingsRef = React.useRef<HTMLDivElement | null>(null);
  // ITT-4 — QualityPanel's "N issues flagged →" passes a bucket; seed the
  // SanityCheckCard filter with it so the jump lands PRE-FILTERED (the card
  // re-seeds itself whenever this prop changes).
  const [findingsFilter, setFindingsFilter] = React.useState<FindingsFilter | undefined>(undefined);
  const sectionRef = React.useRef<HTMLElement | null>(null);

  // Findings-ledger cross-link: select the requested report, pre-filter to
  // issues, and bring the section into view.
  React.useEffect(() => {
    if (!focusReportId) return;
    if (!siteReports.some(r => r.id === focusReportId)) return;
    setSelectedId(focusReportId);
    setFindingsFilter('issues');
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focusNonce re-triggers repeat clicks
  }, [focusReportId, focusNonce]);

  /*
   * DEEP-LINK LANDING (2026-08-14). `?report=` seeded the picker but NEVER
   * scrolled, so "Review quality details" (then on the report's hold banner,
   * since 2026-10-13 a report-header button) — whose whole job is to show
   * the held findings — dropped the operator at
   * the TOP of the Audit tab. This section is the last block on the page,
   * below the trust hero, the resolver/amendments panels and the entire
   * conflicts ledger. The user saw the ledger's (different, live-recomputed)
   * findings and concluded the counts disagreed. Land where the button
   * promised, pre-filtered to the issues, exactly like the ledger path.
   */
  const deepLinkHandled = React.useRef(false);
  React.useEffect(() => {
    if (deepLinkHandled.current) return;
    if (!requestedId || !siteReports.some(r => r.id === requestedId)) return;
    deepLinkHandled.current = true;
    // Report metadata can arrive after the Audit tab mounts. Always select the
    // requested record here instead of relying only on the useState initializer.
    setSelectedId(requestedId);
    setFindingsFilter('issues');
    // Defer past first paint so the section has laid out.
    const t = window.setTimeout(
      () => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      0,
    );
    return () => window.clearTimeout(t);
  }, [requestedId, siteReports]);

  /*
   * `review=truth` is the report-reader's single "Confirm what's true" handoff.
   * Select + scroll above, then open the questions here in Audit. Consume the
   * one-shot parameter so closing the dialog and revisiting Audit does not
   * reopen it unexpectedly; `report=` remains in the URL as durable context.
   */
  const truthDeepLinkHandled = React.useRef(false);
  React.useEffect(() => {
    if (truthDeepLinkHandled.current || requestedReview !== 'truth') return;
    if (!requestedId || report?.id !== requestedId || truthQuestions.length === 0) return;
    truthDeepLinkHandled.current = true;
    setTruthDialogOpen(true);
  }, [requestedId, requestedReview, report?.id, truthQuestions.length]);

  // Persist the decision to the report's first-class USER-STATE doc (store
  // `resolveSanityFinding` — identity-keyed, read back by both the reader and
  // the rerun job) + audit event. Until 2026-08-18 this wrote `sanityFindings`
  // through `updateRagReport`, which on immutable-v2 reports landed in a
  // legacy body object nothing read back — "Will apply on rerun" was inert.
  const resolveSanity = React.useCallback((
    finding: SanityFinding,
    idx: number,
    next: SanityResolutionChange,
  ) => {
    if (!report) return;
    const rawIdx = (report.sanityFindings ?? []).findIndex(
      (f) => sanityFindingIdentity(f) === sanityFindingIdentity(finding),
    );
    const targetIdx = rawIdx >= 0 ? rawIdx : idx;
    void resolveSanityFinding(report.id, targetIdx, {
      resolution: next.resolution,
      userNote: next.userNote,
      applyDirective: next.applyDirective,
    });
    emitAuditEvent(buildSanityResolutionAuditEvent({
      reportId: report.id,
      reportLabel: report.siteName,
      finding,
      nextResolution: next.resolution,
      nextUserNote: next.userNote,
      nextApplyDirective: next.applyDirective,
    }));
  }, [report, resolveSanityFinding]);

  const correctionOpEnvelopesRef = React.useRef<
    Map<
      string,
      {
        operationId: string;
        expectedRevisionId?: string;
        expectedAuditVersion?: number;
        expectedAuditFingerprint?: string;
      }
    >
  >(new Map());

  const resolveCorrectionPreconditions = React.useCallback(
    (
      payloadKey: string,
      explicit?: {
        operationId?: string;
        expectedAuditVersion?: number;
        expectedAuditFingerprint?: string;
      },
    ) => {
      const currentReportRevisionId =
        report?.reportRevision?.revisionId ?? report?.reportRevisionId;
      if (explicit?.operationId?.trim()) {
        const cleanOpId = explicit.operationId.trim();
        const opCacheKey = `op:${cleanOpId}`;
        const existingOpEnvelope =
          correctionOpEnvelopesRef.current.get(opCacheKey) ??
          correctionOpEnvelopesRef.current.get(payloadKey);
        if (existingOpEnvelope && existingOpEnvelope.operationId === cleanOpId) {
          return {
            expectedRevisionId: existingOpEnvelope.expectedRevisionId,
            operationId: existingOpEnvelope.operationId,
            ...(existingOpEnvelope.expectedAuditVersion !== undefined
              ? { expectedAuditVersion: existingOpEnvelope.expectedAuditVersion }
              : {}),
            ...(existingOpEnvelope.expectedAuditFingerprint
              ? { expectedAuditFingerprint: existingOpEnvelope.expectedAuditFingerprint }
              : {}),
          };
        }
        const targetSiteId = siteId ?? report?.siteId;
        const currentSite = targetSiteId
          ? useStore.getState().siteRag.sites.find((s) => s.id === targetSiteId)
          : undefined;
        const expectedAuditVersion =
          explicit.expectedAuditVersion ??
          (currentSite ? computeAuditOverridesVersion(currentSite) : undefined);
        const expectedAuditFingerprint =
          explicit.expectedAuditFingerprint ??
          (currentSite
            ? computeAuditOverridesFingerprint(currentSite.auditOverrides ?? [])
            : undefined);
        const expectedRevisionId =
          (explicit as { expectedRevisionId?: string }).expectedRevisionId ??
          currentReportRevisionId;
        const envelope = {
          expectedRevisionId,
          operationId: cleanOpId,
          ...(expectedAuditVersion !== undefined ? { expectedAuditVersion } : {}),
          ...(expectedAuditFingerprint ? { expectedAuditFingerprint } : {}),
        };
        correctionOpEnvelopesRef.current.set(opCacheKey, envelope);
        correctionOpEnvelopesRef.current.set(payloadKey, envelope);
        return envelope;
      }
      const expectedRevisionId = currentReportRevisionId;
      const existingEnvelope = correctionOpEnvelopesRef.current.get(payloadKey);
      if (existingEnvelope) {
        return {
          expectedRevisionId: existingEnvelope.expectedRevisionId,
          operationId: existingEnvelope.operationId,
          ...(existingEnvelope.expectedAuditVersion !== undefined
            ? { expectedAuditVersion: existingEnvelope.expectedAuditVersion }
            : {}),
          ...(existingEnvelope.expectedAuditFingerprint
            ? { expectedAuditFingerprint: existingEnvelope.expectedAuditFingerprint }
            : {}),
        };
      }
      const targetSiteId = siteId ?? report?.siteId;
      const currentSite = targetSiteId
        ? useStore.getState().siteRag.sites.find((s) => s.id === targetSiteId)
        : undefined;
      const expectedAuditVersion =
        explicit?.expectedAuditVersion ??
        (currentSite ? computeAuditOverridesVersion(currentSite) : undefined);
      const expectedAuditFingerprint =
        explicit?.expectedAuditFingerprint ??
        (currentSite
          ? computeAuditOverridesFingerprint(currentSite.auditOverrides ?? [])
          : undefined);
      const operationId = generateAuditOperationId(
        `report-correction:${report?.id ?? 'report'}`,
      );
      const envelope = {
        expectedRevisionId,
        operationId,
        ...(expectedAuditVersion !== undefined ? { expectedAuditVersion } : {}),
        ...(expectedAuditFingerprint ? { expectedAuditFingerprint } : {}),
      };
      correctionOpEnvelopesRef.current.set(payloadKey, envelope);
      return envelope;
    },
    [report?.id, report?.reportRevision?.revisionId, report?.reportRevisionId, report?.siteId, siteId],
  );

  const applySanityCorrectionNow = React.useCallback(async (
    finding: SanityFinding,
    _idx: number,
    input: SanityApplyNowInput,
  ) => {
    if (!report || typeof applySourceSupportedCorrectionToReport !== 'function') {
      return { applied: false, error: 'Correction workflow is unavailable.' };
    }
    const complaintCheck = isDiagnosticComplaintNotReplacement(
      input.replacementText,
      [finding.finding ?? ''].filter(Boolean),
    );
    if (complaintCheck.isComplaint) {
      return {
        applied: false,
        reason: 'diagnostic-complaint-not-replacement',
        error: complaintCheck.detail,
      };
    }
    const fieldPath = resolveReportFieldPathForCorrection(
      report,
      input.fieldPath ?? finding.section,
      finding.finding,
      input.targetSubstring,
    );
    if (fieldPath.startsWith('ambiguous:')) {
      const candidates = fieldPath.slice('ambiguous:'.length);
      return {
        applied: false,
        reason: 'ambiguous-field-path',
        error: `This finding spans multiple report fields (${candidates}). Open "Edit suggestion", specify a single target fieldPath and replacement prose, or use Rerun.`,
      };
    }
    if (fieldPath.startsWith('wrong-field:') || !areSectionAndFieldPathCompatible(finding.section, fieldPath)) {
      return {
        applied: false,
        reason: 'wrong-field-mapping',
        error: `Target field "${fieldPath}" does not match section "${finding.section}". Open "Edit suggestion" and specify the exact target fieldPath.`,
      };
    }
    const findingIdKey = sanityFindingIdentity(finding);
    const findingAnchorPaths =
      input.applyDirective?.anchorFactPaths ??
      finding.applyDirective?.anchorFactPaths ??
      (finding as { anchorFactPaths?: string[] }).anchorFactPaths;
    const correctionItems = [
      {
        fieldPath,
        ...(input.targetSubstring ? { targetSubstring: input.targetSubstring } : {}),
        replacementText: input.replacementText,
        evidenceCitation: enrichCitationFromReportReferences(report, input.evidenceCitation),
        ...(findingAnchorPaths && findingAnchorPaths.length > 0
          ? { anchorFactPaths: findingAnchorPaths }
          : {}),
        ...(finding.evidenceSourceFields
          ? { evidenceSourceFields: finding.evidenceSourceFields }
          : {}),
        ...(finding.evidenceSourceIds && finding.evidenceSourceIds.length > 0
          ? { evidenceSourceIds: finding.evidenceSourceIds }
          : {}),
        ...(finding.evidenceSourceRevision !== undefined
          ? { evidenceSourceRevision: finding.evidenceSourceRevision }
          : {}),
        sanityFindingIdentity: findingIdKey,
        targetSanityFindingMatches: [finding.finding, findingIdKey].filter(Boolean),
        category: finding.category,
        resolution: 'apply' as const,
        section: finding.section,
        ...(input.userNote ? { userNote: input.userNote } : {}),
      },
    ];
    const payloadKey = `sanity:${report.id}:${findingIdKey}:${JSON.stringify(correctionItems)}`;
    const preconditions = resolveCorrectionPreconditions(payloadKey);
    const result = await applySourceSupportedCorrectionToReport(
      report.id,
      correctionItems,
      preconditions,
    );
    if (result.applied || result.conflict) {
      correctionOpEnvelopesRef.current.delete(payloadKey);
      if (preconditions.operationId) {
        correctionOpEnvelopesRef.current.delete(`op:${preconditions.operationId}`);
      }
    }
    if (result.applied) {
      emitAuditEvent(buildSanityResolutionAuditEvent({
        reportId: report.id,
        reportLabel: report.siteName,
        finding,
        nextResolution: 'apply',
        nextUserNote: input.userNote,
        nextApplyDirective: input.applyDirective,
      }));
    }
    return result;
  }, [report, applySourceSupportedCorrectionToReport, resolveCorrectionPreconditions]);

  const retryFactCheck = React.useCallback(async (reportId: string): Promise<void> => {
    const current = useStore.getState().siteRag.reports.find(r => r.id === reportId);
    if (!current) throw new Error('report not found');
    const currentOwnerUid =
      (current as unknown as { _ownerUid?: string; ownerUid?: string; reportOwnerUid?: string; requesterUid?: string })._ownerUid ??
      (current as unknown as { ownerUid?: string }).ownerUid ??
      (current as unknown as { reportOwnerUid?: string }).reportOwnerUid ??
      (current as unknown as { requesterUid?: string }).requesterUid;
    const expectedRevisionId =
      current.reportRevision?.revisionId ?? current.reportRevisionId;
    if (expectedRevisionId) {
      const res = await authedFetch(
        '/api/reports/' + encodeURIComponent(reportId) + '/audit/revalidate',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            expectedRevisionId,
            ...(currentOwnerUid ? { reportOwnerUid: currentOwnerUid } : {}),
          }),
        },
      );
      const payload = (await res.json().catch(() => ({}))) as {
        applied?: boolean;
        noOpRevalidate?: boolean;
        report?: SiteRagReport;
        error?: string;
        detail?: string;
      };
      if (!res.ok || !payload.applied || !payload.report) {
        throw new Error(
          payload.detail ?? payload.error ?? 'Audit revalidation failed (HTTP ' + res.status + ')',
        );
      }
      const nextReportWithOwner = currentOwnerUid
        ? ({ ...payload.report, _ownerUid: currentOwnerUid, ...(payload.noOpRevalidate ? { _lastNoOpRevalidate: true } : { _lastNoOpRevalidate: false }) } as SiteRagReport)
        : ({ ...payload.report, ...(payload.noOpRevalidate ? { _lastNoOpRevalidate: true } : { _lastNoOpRevalidate: false }) } as SiteRagReport);
      useStore.setState((state) => ({
        siteRag: {
          ...state.siteRag,
          reports: state.siteRag.reports.map((existing) =>
            existing.id === reportId ? nextReportWithOwner : existing,
          ),
        },
      }));
      return;
    }
    const { rerunReportFactCheck } = await import('../../services/reportFactCheck');
    const result = rerunReportFactCheck(current);
    await updateRagReport(reportId, { factCheck: result }, { strictPersist: true });
  }, [updateRagReport]);

  const applyTruthReportCorrection = React.useCallback(async (
    items: TruthResolutionApplyCorrectionItem[],
    options?: {
      operationId?: string;
      expectedRevisionId?: string;
      expectedAuditVersion?: number;
      expectedAuditFingerprint?: string;
      expectedSedVersion?: number;
    },
  ) => {
    if (!report || typeof applySourceSupportedCorrectionToReport !== 'function') {
      return { applied: false, error: 'Correction workflow is unavailable.' };
    }
    const corrections = items.map((item) => {
      const fieldPath = resolveReportFieldPathForCorrection(
        report,
        undefined,
        item.originalClaim,
        item.originalClaim,
      );
      return {
        fieldPath,
        targetSubstring: item.originalClaim,
        replacementText: item.replacementText,
        evidenceCitation: enrichCitationFromReportReferences(report, item.evidenceCitation || '[Ref 1]'),
        ...(item.evidenceDocumentId ? { evidenceSourceIds: [item.evidenceDocumentId] } : {}),
        ...(item.sourceType ? { evidenceSourceFields: { sourceType: item.sourceType } } : {}),
        resolution: 'apply' as const,
        userNote: [
          item.assumptionLabel ? `[Assumption: ${item.assumptionLabel}]` : '',
          item.rationale,
          item.evidenceAttachment ? `[Evidence: ${item.evidenceAttachment}]` : '',
        ].filter(Boolean).join(' — '),
      };
    });
    const payloadKey = `truth:${report.id}:${JSON.stringify(corrections)}`;
    const preconditions = resolveCorrectionPreconditions(
      payloadKey,
      options,
    );
    const result = await applySourceSupportedCorrectionToReport(
      report.id,
      corrections,
      preconditions,
    );
    if (result.applied || result.conflict) {
      correctionOpEnvelopesRef.current.delete(payloadKey);
    }
    return result;
  }, [report, applySourceSupportedCorrectionToReport, resolveCorrectionPreconditions]);

  const combinedSanityFindings = React.useMemo<SanityFinding[]>(() => {
    const narrativeTexts = report ? collectNarrativeStrings(report) : [];
    const base = (report?.sanityFindings ?? []).filter(
      (f) => !isFalsePositiveQueueDurationFindingText(f.finding ?? '', narrativeTexts),
    );
    const reviewIssues = report?.qualityReview?.issues ?? [];
    const declaredIssueCount =
      typeof report?.qualityReview?.issueCount === 'number' &&
      Number.isFinite(report.qualityReview.issueCount)
        ? Math.max(0, Math.trunc(report.qualityReview.issueCount))
        : 0;
    if (reviewIssues.length === 0 && declaredIssueCount === 0) return base;
    const existingTexts = new Set(base.map(f => (f.finding ?? '').trim().toLowerCase()));
    const synthesized: SanityFinding[] = [];
    const normalizeIssueSeverity = (raw: unknown): 'high' | 'medium' | 'low' => {
      const s = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
      if (s === 'high' || s === 'major' || s === 'critical' || s === 'block') return 'high';
      if (s === 'medium' || s === 'moderate' || s === 'warn' || s === 'warning') return 'medium';
      if (s === 'low' || s === 'minor' || s === 'info') return 'low';
      return 'high';
    };
    reviewIssues.forEach((issue, idx) => {
      const rawIssue = (issue ?? {}) as {
        issue?: unknown;
        description?: unknown;
        message?: unknown;
        severity?: unknown;
        section?: unknown;
        suggestion?: unknown;
        fix?: unknown;
      };
      const primaryText =
        typeof rawIssue.issue === 'string' && rawIssue.issue.trim().length > 0
          ? rawIssue.issue.trim()
          : typeof rawIssue.description === 'string' && rawIssue.description.trim().length > 0
            ? rawIssue.description.trim()
            : typeof rawIssue.message === 'string' && rawIssue.message.trim().length > 0
              ? rawIssue.message.trim()
              : '';
      const text =
        primaryText ||
        `Flagged quality review issue (#${idx + 1}) has no stored description text on this report record.`;
      const joinedNarrative = narrativeTexts.join('\n');
      if (
        isFalsePositiveQueueDurationFindingText(text, narrativeTexts) ||
        isReviewIssueAlreadyQualifiedInText(text, joinedNarrative, report) ||
        (/water-use screen check flagged out-of-scope load bindings/i.test(text) &&
          truthQuestions.every((q) => q.detector !== 'waterUseScreening'))
      ) {
        return;
      }
      if (existingTexts.has(text.toLowerCase())) return;
      existingTexts.add(text.toLowerCase());
      const suggestionText =
        typeof rawIssue.suggestion === 'string' && rawIssue.suggestion.trim().length > 0
          ? rawIssue.suggestion.trim()
          : typeof rawIssue.fix === 'string' && rawIssue.fix.trim().length > 0
            ? rawIssue.fix.trim()
            : 'Inspect the section narrative against current site evidence, edit a source-supported replacement, or rerun the audit.';
      synthesized.push({
        id: `qr-issue-${idx}`,
        category: 'inconsistency',
        severity: normalizeIssueSeverity(rawIssue.severity),
        section:
          typeof rawIssue.section === 'string' && rawIssue.section.trim().length > 0
            ? rawIssue.section.trim()
            : 'executiveSummary',
        finding: text,
        suggestion: suggestionText,
      });
    });
    const unresolvedBaseCount = base.filter(
      f => (!f.resolution || f.resolution === 'apply') && !isVerifiedSanityCategory(f.category) && (f.severity as string) !== 'verified',
    ).length;
    const joinedNarrativeAll = narrativeTexts.join('\n');
    const suppressedReviewIssuesCount = reviewIssues.filter((issue) => {
      const rec = (issue ?? {}) as { issue?: unknown; description?: unknown; message?: unknown };
      const t = String(rec.issue ?? rec.description ?? rec.message ?? '');
      return (
        isFalsePositiveQueueDurationFindingText(t, narrativeTexts) ||
        isReviewIssueAlreadyQualifiedInText(t, joinedNarrativeAll, report) ||
        (/water-use screen check flagged out-of-scope load bindings/i.test(t) &&
          truthQuestions.every((q) => q.detector !== 'waterUseScreening'))
      );
    }).length;
    const totalRepresentedIssues = unresolvedBaseCount + synthesized.length + suppressedReviewIssuesCount;
    for (let missingIdx = totalRepresentedIssues; missingIdx < declaredIssueCount; missingIdx += 1) {
      const fallbackText = `Flagged quality review issue (#${missingIdx + 1}) has no stored detail record on this report.`;
      if (existingTexts.has(fallbackText.toLowerCase())) continue;
      existingTexts.add(fallbackText.toLowerCase());
      synthesized.push({
        id: `qr-issue-missing-${missingIdx}`,
        category: 'inconsistency',
        severity: 'high',
        section: 'executiveSummary',
        finding: fallbackText,
        suggestion: 'Rerun the report quality audit or inspect the report narrative against site evidence.',
      });
    }
    return [...base, ...synthesized];
  }, [report?.sanityFindings, report?.qualityReview?.issues, report?.qualityReview?.issueCount, truthQuestions]);

  const [auditRerunStatus, setAuditRerunStatus] = React.useState<string | null>(null);
  const handleRerunAudit = React.useCallback(async () => {
    if (!report) return;
    setAuditRerunStatus('Re-evaluating audit & fact-check…');
    try {
      await retryFactCheck(report.id);
      const latest = useStore.getState().siteRag.reports.find((r) => r.id === report.id) as
        | (SiteRagReport & { _lastNoOpRevalidate?: boolean })
        | undefined;
      if (latest?._lastNoOpRevalidate) {
        setAuditRerunStatus(
          'Audit re-evaluated: no changes detected (existing report revision and hold status unchanged).',
        );
      } else if (!latest?.qualityReview) {
        setAuditRerunStatus(
          'Deterministic audit & fact-check re-evaluated and saved (AI narrative reviewer verdict remains absent; use Rerun report to generate a new AI review).',
        );
      } else {
        setAuditRerunStatus('Audit re-evaluated and saved against current site evidence & overrides.');
      }
    } catch (err) {
      setAuditRerunStatus(`Audit rerun error: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [report, retryFactCheck]);

  if (siteReports.length === 0) return null;

  // Queued corrections (f.resolution === 'apply', "Will apply on rerun") remain open
  // until a report rerun or immediate source-supported edit applies them to the narrative.
  const unresolvedCount = combinedSanityFindings.filter(f => (!f.resolution || f.resolution === 'apply') && !isVerifiedSanityCategory(f.category) && (f.severity as string) !== 'verified').length + truthQuestions.length;
  const ledgerEntries = Object.values(report?.approvedFactualCorrections?.entries ?? {});
  const appliedInThisRunCount = ledgerEntries.filter(
    (entry) =>
      entry &&
      entry.status === 'active' &&
      entry.lastEvaluationStatus === 'resolved' &&
      (!entry.originReportId || entry.originReportId === report?.id) &&
      !isDiagnosticComplaintNotReplacement(entry.appliedDirective ?? '', [entry.finding ?? '']).isComplaint,
  ).length;
  const historicalCarriedOverCount =
    ledgerEntries.filter(
      (entry) =>
        entry &&
        entry.originReportId &&
        entry.originReportId !== report?.id &&
        !isDiagnosticComplaintNotReplacement(entry.appliedDirective ?? '', [entry.finding ?? '']).isComplaint,
    ).length +
    combinedSanityFindings.filter(
      (f) => f.resolution === 'ignore' && !isVerifiedSanityCategory(f.category) && (f.severity as string) !== 'verified',
    ).length;
  const unresolvedAndHoldCount = unresolvedCount + (report?.qualityHoldFindings?.length ?? 0);

  return (
    <section
      ref={sectionRef}
      data-testid="report-quality-audit-section"
      aria-labelledby="report-quality-audit-title"
    >
      {/* Same da-band section label the findings ledger uses — one visual
          grammar for every major block on the Audit tab. */}
      <div className="da-band">
        <span className="da-band__label" id="report-quality-audit-title">Report quality</span>
        <span className="da-band__rule" />
        <span className="da-band__count">
          {siteReports.length} report{siteReports.length === 1 ? '' : 's'}
          {unresolvedCount > 0 ? ` · ${unresolvedCount} unresolved finding${unresolvedCount === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      <div className="rounded-lg border border-border bg-card shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileSearch className="w-4 h-4 text-primary shrink-0" aria-hidden />
          <p className="text-xs text-text-muted">
            Sanity findings, fact-check, and generation quality for the
            selected report. Resolutions persist to the report and steer its
            next rerun.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-text-muted">
            Report
            <select
              className="rounded-md border border-border bg-background px-2 py-1 text-xs text-text-primary"
              value={report?.id ?? ''}
              onChange={e => {
                setSelectedId(e.target.value);
                setHasDurableReportContext(true);
              }}
              data-testid="report-quality-audit-picker"
            >
              {siteReports.map(r => (
                <option key={r.id} value={r.id}>
                  {(r.reportType ?? 'report')} · {new Date(r.createdAt).toLocaleDateString()}
                </option>
              ))}
            </select>
          </label>
          {report && (
            <button
              type="button"
              onClick={() => { void handleRerunAudit(); }}
              className="rounded-md border border-border px-2 py-1 text-xs text-text-secondary hover:text-primary hover:border-primary/50"
              data-testid="report-quality-rerun-audit"
            >
              Rerun audit
            </button>
          )}
          {onViewReport && report && (
            <button
              type="button"
              onClick={() => onViewReport(report.id)}
              className="rounded-md border border-border px-2 py-1 text-xs text-text-secondary hover:text-primary hover:border-primary/50"
              data-testid="report-quality-audit-open"
            >
              Open report ↗
            </button>
          )}
        </div>
      </div>
      {auditRerunStatus && (
        <div
          className="border-b border-border-subtle bg-surface-2 px-4 py-2 text-xs text-text-secondary"
          data-testid="report-quality-rerun-audit-status"
          role="status"
        >
          {auditRerunStatus}
        </div>
      )}
      {report && (
        <div className="space-y-4 p-4">
          {(appliedInThisRunCount > 0 || historicalCarriedOverCount > 0 || unresolvedAndHoldCount > 0) && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-md border border-border-subtle bg-surface-2 px-3 py-2 text-[11px] text-text-secondary"
              data-testid="report-quality-decision-breakdown"
            >
              <span data-testid="audit-count-applied-this-run">
                <strong className="text-text-primary">Applied in this run:</strong> {appliedInThisRunCount}
              </span>
              <span aria-hidden="true">·</span>
              <span data-testid="audit-count-historical-carried">
                <strong className="text-text-primary">Historical carried-over decisions:</strong> {historicalCarriedOverCount}
              </span>
              <span aria-hidden="true">·</span>
              <span data-testid="audit-count-unresolved-holds">
                <strong className="text-text-primary">Unresolved findings / holds:</strong> {unresolvedAndHoldCount}
              </span>
            </div>
          )}
          {truthQuestions.length > 0 && (
            <div
              className="flex flex-col gap-3 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              data-testid="report-quality-truth-questions"
            >
              <div className="min-w-0">
                <p className="type-body font-semibold text-text-primary">
                  {truthQuestions.length} statement{truthQuestions.length === 1 ? '' : 's'} need confirmation
                </p>
                <p className="type-body text-text-secondary">
                  Resolve the disagreement here so the answer becomes part of the site evidence record.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => setTruthDialogOpen(true)}
                data-testid="report-quality-confirm-truth"
              >
                Confirm what's true
              </Button>
            </div>
          )}
          <QualityPanel
            report={report}
            openTruthQuestionsCount={truthQuestions.length}
            onJumpToFindings={(bucket) => {
              setFindingsFilter(bucket);
              findingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            onRetryFactCheck={retryFactCheck}
          />
          <div ref={findingsRef}>
            <SanityCheckCard
              findings={combinedSanityFindings}
              report={report}
              onResolve={resolveSanity}
              onApplyNow={applySanityCorrectionNow}
              initialFilter={findingsFilter}
              forceExpanded={findingsFilter !== undefined ? true : undefined}
            />
          </div>
          {isAdmin && (
            <GenerationCostPanel
              telemetry={report.generationTelemetry}
              reviewTrace={report.reviewTrace}
              isAdmin={isAdmin}
            />
          )}
          {(truthQuestions.length > 0 || truthDialogOpen) && (
            <TruthResolutionDialog
              open={truthDialogOpen}
              onOpenChange={setTruthDialogOpen}
              questions={truthQuestions}
              sed={undefined}
              siteId={siteId ?? report.siteId}
              reportTypeId={report.reportType}
              reportRevisionId={report.reportRevision?.revisionId ?? report.reportRevisionId}
              siteDocuments={scopedSiteDocuments}
              onApplyReportCorrection={applyTruthReportCorrection}
              onRefreshBaseline={async () => {
                const targetSiteId = siteId ?? report.siteId;
                if (!targetSiteId) {
                  throw new Error('Cannot refresh baseline: missing siteId.');
                }
                const refreshFn = (useStore.getState() as {
                  refreshSiteBaseline?: (id: string) => Promise<{
                    site?: {
                      auditOverrides?: import('../../types/site').SiteAuditOverride[];
                      auditOverridesVersion?: number;
                    } | null;
                    sed?: { id: string; version: number } | null;
                  }>;
                }).refreshSiteBaseline;
                if (typeof refreshFn !== 'function') {
                  throw new Error('Authoritative baseline refresh is unavailable.');
                }
                const refreshed = await refreshFn(targetSiteId);
                if (!refreshed?.site) {
                  throw new Error('Authoritative site baseline could not be loaded.');
                }
                return {
                  sed: refreshed.sed ? { id: refreshed.sed.id, version: refreshed.sed.version } : undefined,
                  auditOverrides: refreshed.site.auditOverrides,
                  auditOverridesVersion: refreshed.site.auditOverridesVersion,
                };
              }}
              onRerun={onRerunReport ? () => onRerunReport(report.id) : undefined}
            />
          )}
        </div>
      )}
      </div>
    </section>
  );
}
